import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import { v4 as uuid } from 'uuid';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import Auth from '@Services/Auth';
import AppStorage from '@Services/AppStorage';
import PropTypes from 'prop-types';
import { Modal } from '@els/els-react--modal';
import { Button } from '@els/els-react--button';
import { LinkElement } from '@els/els-react--link-element';
import { Banner } from '@els/els-react--banner';
import { ValidatedTextInput } from '@els/els-react--text-input';
import { sanitizeDiscard } from '@Services/HtmlSanitizeService';
import StructureThumbnailContainer from '@Containers/StructureThumbnail';
import { open as openStructureDialog } from '@Containers/StructureDialog/StructureDialogAction';
import parseV3000SumFormula from '@Utils/React/MolFiletoFormula';
import { historyReducerKey } from '../Constants';
import { handleNameChange, onSubmitForm, closeSaveModal } from '../HistoryAction';
import ConfirmationPopup from '../ConfirmationPopup';
import { trackAdobeAnalyticsError, trackConversionDriverClick, trackHistoryPopupSaveSearchButtonClick } from '../analytics';

import './SaveModal.scss';

const QUERY_PREVIEW_LIMIT = 80;
const HISTORY_LOGIN_REDIRECT_PATH_STORAGE_KEY = 'historyLoginRedirectPath';
// data-e2e is spread onto the Leyden <input> itself, so target the input directly.
const NAME_INPUT_SELECTOR = 'input[data-e2e="history-save-modal-name-input"]';

function getStructureNameForDialog(structure) {
  if (!structure) {
    return undefined;
  }

  const formula = parseV3000SumFormula(structure);
  if (!formula) {
    return undefined;
  }

  return {
    name: formula,
    isLinearFormula: true,
  };
}

function SaveModal({ closed, selfClose }) {
  const { saveModalTitle, saveModalNameInput, isInputTouched, itemToBeSaved, isSaveInProgress, showSaveModal } = useSelector((state) =>
    state.get(historyReducerKey)
  );
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isQueryExpanded, setIsQueryExpanded] = useState(false);
  const dispatch = useDispatch();
  const trimmedName = saveModalNameInput.trim();
  const errorTitle = 'Login required';
  const errorMessage = 'Please sign-in/register to save your History.';
  const isRename = saveModalTitle === 'Rename';
  const queryText = sanitizeDiscard(itemToBeSaved?.comment || '', { allowedTags: [] });
  const structureForPreview =
    itemToBeSaved?.quickSearchItem?.structure || itemToBeSaved?.structure || itemToBeSaved?.structures?.[0]?.structure;
  const structureNameForDialog = getStructureNameForDialog(structureForPreview);
  const shouldShowQueryToggle = queryText.length > QUERY_PREVIEW_LIMIT;
  const displayedQueryText =
    isQueryExpanded || !shouldShowQueryToggle ? queryText : `${queryText.slice(0, QUERY_PREVIEW_LIMIT).trimEnd()}...`;

  useEffect(() => {
    if (!showSaveModal) {
      setIsModalOpen(false);
    }
  }, [showSaveModal]);

  useEffect(() => {
    if (showSaveModal) {
      setIsQueryExpanded(false);
    }
  }, [showSaveModal, itemToBeSaved?.resultName]);

  useEffect(() => {
    if (closed) {
      setIsModalOpen(false);
      dispatch(closeSaveModal());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [closed]);

  const isSaveDisabled = () => isSaveInProgress || trimmedName === '' || trimmedName.length < 3 || trimmedName.length > 50;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isSaveDisabled()) return;
    trackHistoryPopupSaveSearchButtonClick();
    dispatch(onSubmitForm(itemToBeSaved, trimmedName, isRename));
  };

  const resolveFormError = () => {
    if (isInputTouched && !trimmedName) {
      return 'Name is required.';
    }
    if (trimmedName.length < 3) {
      return 'Name is too short, minimum length is 3 characters.';
    }
    if (trimmedName.length > 50) {
      return 'Name is too long, maximum length is 50 characters.';
    }
    return '';
  };

  const resolveNameValidationMessage = (valueOrEvent) => {
    const currentValue = typeof valueOrEvent === 'string' ? valueOrEvent : valueOrEvent?.target?.value || '';
    const trimmedValue = currentValue.trim();

    if (!trimmedValue) {
      return isInputTouched ? 'Name is required.' : null;
    }

    if (trimmedValue.length < 3) {
      return 'Name is too short, minimum length is 3 characters.';
    }

    if (trimmedValue.length > 50) {
      return 'Name is too long, maximum length is 50 characters.';
    }

    return null;
  };

  const validationMessage = resolveNameValidationMessage(saveModalNameInput);

  const trackError = () => {
    const formError = resolveFormError();
    trackAdobeAnalyticsError(formError);
  };

  const handleConfirmSignIn = () => {
    dispatch(closeSaveModal());
    AppStorage.instance.setToSessionStorage(
      HISTORY_LOGIN_REDIRECT_PATH_STORAGE_KEY,
      history.location?.pathname?.includes('saved') ? paths.HISTORY_SAVED : paths.HISTORY_RECENT
    );
    history.push({
      pathname: generatePath(paths.LOGIN),
    });
  };

  if (Auth.isAnonymousUser()) {
    return (
      <ConfirmationPopup
        title={errorTitle}
        text={errorMessage}
        submitButtonText="Sign in"
        dismissButtonText="Cancel"
        isCancelButton
        onClose={() => {
          dispatch(closeSaveModal());
        }}
        onConfirm={() => {
          trackConversionDriverClick('history:savesearch:sign-in');
          AppStorage.instance.set('scheduledHistory', itemToBeSaved);
          handleConfirmSignIn();
        }}
        closed={closed}
        selfClose={selfClose}
      />
    );
  }

  return (
    isModalOpen && (
      <Modal
        label={isRename ? 'Rename this search' : 'Save this search'}
        focusTrapProps={{
          focusTrapOptions: {
            // Land initial focus on the name field instead of the dialog container.
            initialFocus: () => document.querySelector(NAME_INPUT_SELECTOR),
          },
        }}
        onClose={() => {
          setIsModalOpen(false);
          dispatch(closeSaveModal());
        }}>
        <header className="save-modal__header">
          <h3 className="u-els-margin-bottom">{isRename ? 'Rename this search' : 'Save this search'}</h3>
        </header>

        <form onSubmit={handleSubmit}>
          <section className="save-modal__content">
            {isSaveInProgress && (
              <div className="save-modal__banner">
                <Banner type="alert" isVisible hideCloseLabel>
                  Processing your request. This may take a few minutes.
                </Banner>
              </div>
            )}
            {queryText && (
              <div className="save-modal__row save-modal__row--query">
                <div className="save-modal__label-column">
                  <span className="save-modal__label" aria-hidden="true">
                    Query:
                  </span>
                </div>
                <div className="save-modal__value-column">
                  <div className="u-els-display-flex query-text-container save-modal__query-preview">
                    <span className="save-modal__query-text">{displayedQueryText}</span>
                    {structureForPreview && (
                      <Button
                        data-e2e="save-modal-structure-preview-button"
                        type="secondary"
                        aria-label="Structure preview"
                        className="query-text-structure-button u-els-padding-none"
                        onClick={() => {
                          dispatch(openStructureDialog(uuid(), structureForPreview, structureNameForDialog));
                        }}>
                        <StructureThumbnailContainer enableThumbnail structure={structureForPreview} thumbnailType="history" />
                      </Button>
                    )}
                  </div>
                  {shouldShowQueryToggle && (
                    <LinkElement
                      href="#"
                      inline
                      navigation
                      noExternalIcon
                      className="save-modal__query-toggle"
                      data-e2e="history-save-modal-query-toggle"
                      onClick={(event) => {
                        event.preventDefault();
                        setIsQueryExpanded((currentValue) => !currentValue);
                      }}>
                      {isQueryExpanded ? 'Show less' : 'Show all'}
                    </LinkElement>
                  )}
                </div>
              </div>
            )}

            <div className="save-modal__row">
              <div className="save-modal__label-column">
                <span className="save-modal__label" aria-hidden="true">
                  {isRename ? 'Rename as:' : 'Save as:'}
                </span>
              </div>
              <div className="save-modal__value-column">
                <ValidatedTextInput
                  id="searchName"
                  name="searchName"
                  data-e2e="history-save-modal-name-input"
                  className="save-modal__input"
                  value={saveModalNameInput || ''}
                  onChange={(e) => dispatch(handleNameChange(e?.target?.value))}
                  required
                  label="Name"
                  placeholder="Type a name"
                  min={3}
                  max={50}
                  messages={{ REQUIRED_ERROR_MESSAGE: 'Name is required.' }}
                  validator={resolveNameValidationMessage}
                  key="searchNameKey"
                  onBlur={() => {
                    trackError();
                  }}
                />
                <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">
                  {validationMessage || ''}
                </div>
              </div>
            </div>
          </section>

          <footer className="save-modal__footer">
            <div className="save-modal__button-container">
              <Button
                className="e2e-save-modal-cancel"
                type="secondary"
                data-e2e="history-save-modal-cancel-button"
                onClick={() => {
                  setIsModalOpen(false);
                  dispatch(closeSaveModal());
                }}>
                Cancel
              </Button>
              <Button
                className="e2e-save-modal-save"
                type="primary"
                data-e2e="history-save-modal-save-button"
                htmlType="submit"
                disabled={isSaveDisabled()}>
                Save
              </Button>
            </div>
          </footer>
        </form>
      </Modal>
    )
  );
}

SaveModal.propTypes = {
  selfClose: PropTypes.func,
  closed: PropTypes.bool,
};

export default SaveModal;
